import { useEffect, useRef, useState } from 'react';
import { usePage } from '@inertiajs/react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePublicDesignSettings } from '@/hooks/use-public-design-settings';
import { useSiteButtons } from '@/hooks/use-site-buttons';
import { cmsPagesPreviewLegalCookiePolicyPath, publicCookiePolicyPath } from '@/lib/routes';
import { addWindowEventListener, getDocument, parseUrl } from '@/lib/utils';
import type { SharedData } from '@/types/shared';
import { CookieBannerText } from './cookie-banner-text';

const CONSENT_COOKIE_NAME = 'll_cookie_consent';
const CONSENT_COOKIE_VALUE = 'accepted';
const EXPIRY_DAYS = 365;
const PREVIEW_TOOLTIP_DURATION_MS = 4000;
const PREVIEW_TOOLTIP =
  'This banner is a sample of what will appear on your live website and cannot be closed while in preview mode.';

function isConsentStored(): boolean {
  const cookie = getDocument()?.cookie ?? '';
  const consentCookie = cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_COOKIE_NAME}=`));

  return consentCookie === `${CONSENT_COOKIE_NAME}=${CONSENT_COOKIE_VALUE}`;
}

function storeConsent(): void {
  const doc = getDocument();
  if (!doc) return;

  const maxAge = EXPIRY_DAYS * 24 * 60 * 60;
  const secure = doc.location.protocol === 'https:' ? '; Secure' : '';
  doc.cookie = `${CONSENT_COOKIE_NAME}=${CONSENT_COOKIE_VALUE}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

function getPreviewUrlParams(url: string | undefined) {
  const { searchParams } = parseUrl(url || '');
  const currentParams = searchParams.toString();

  return currentParams ? `?${currentParams}` : '';
}

export function CookieBanner() {
  const { url, props } = usePage<SharedData & { previewMode?: boolean }>();
  const { site, previewMode } = props;
  const { themeVariables } = usePublicDesignSettings();
  const { shape, buttonStyle } = useSiteButtons();
  const bannerText = site?.legalNotice?.cookieBannerText;
  const shouldShowBanner = site?.legalNotice?.showCookieBanner === true && Boolean(bannerText);
  const isPreviewMode = previewMode === true;
  const [visible, setVisible] = useState(false);
  const [previewTooltipOpen, setPreviewTooltipOpen] = useState(false);
  const previewTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cookiePolicyHref = isPreviewMode
    ? `${cmsPagesPreviewLegalCookiePolicyPath()}${getPreviewUrlParams(url)}`
    : publicCookiePolicyPath();

  useEffect(() => {
    if (!shouldShowBanner) {
      setVisible(false);
      return;
    }

    setVisible(isPreviewMode || !isConsentStored());
  }, [isPreviewMode, shouldShowBanner]);

  useEffect(() => {
    return () => {
      if (previewTooltipTimerRef.current) {
        clearTimeout(previewTooltipTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!previewTooltipOpen) return undefined;

    const removePointerDownListener =
      addWindowEventListener<PointerEvent>('pointerdown', () => {
        setPreviewTooltipOpen(false);
      }) ?? undefined;
    const removeFocusInListener =
      addWindowEventListener<FocusEvent>('focusin', () => {
        setPreviewTooltipOpen(false);
      }) ?? undefined;

    return () => {
      removePointerDownListener?.();
      removeFocusInListener?.();
    };
  }, [previewTooltipOpen]);

  if (!visible) return null;

  const handleAccept = () => {
    if (isPreviewMode) {
      if (previewTooltipTimerRef.current) {
        clearTimeout(previewTooltipTimerRef.current);
      }

      setPreviewTooltipOpen(true);
      previewTooltipTimerRef.current = setTimeout(() => {
        setPreviewTooltipOpen(false);
        previewTooltipTimerRef.current = null;
      }, PREVIEW_TOOLTIP_DURATION_MS);
      return;
    }

    storeConsent();
    setVisible(false);
  };

  return (
    <Sheet open={visible} modal={false}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        showOverlay={false}
        data-theme-root
        className="inset-x-4 bottom-5 mx-auto max-w-5xl rounded-md border bg-background px-5 py-4 shadow-xl"
        style={themeVariables}
      >
        <SheetTitle className="sr-only">Cookie consent</SheetTitle>
        <SheetDescription className="sr-only">
          Review the cookie notice and accept to continue.
        </SheetDescription>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CookieBannerText text={bannerText || ''} cookiePolicyHref={cookiePolicyHref} />
          {isPreviewMode ? (
            <Tooltip open={previewTooltipOpen} onOpenChange={setPreviewTooltipOpen}>
              <div className="relative shrink-0 self-center sm:self-auto">
                <Button onClick={handleAccept} shape={shape} buttonStyle={buttonStyle}>
                  Accept
                </Button>
                <TooltipTrigger asChild>
                  <span aria-hidden="true" className="pointer-events-none absolute inset-0" />
                </TooltipTrigger>
              </div>
              <TooltipContent
                side="top"
                sideOffset={12}
                className="max-w-xs border bg-background px-5 py-4 text-center text-sm leading-snug text-foreground shadow-lg [&_svg]:bg-background [&_svg]:fill-background"
              >
                {PREVIEW_TOOLTIP}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              onClick={handleAccept}
              shape={shape}
              buttonStyle={buttonStyle}
              className="shrink-0 self-center sm:self-auto"
            >
              Accept
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
